import { createAsyncState } from 'async-utils';
import { Nullable } from 'ox-common-types';
import {
  ConditionalFilter,
  FilterIssueType,
  FilterItems,
  SearchInput,
  getNumberOfSelectedFilters,
} from 'ox-filter-utils';
import { createStore, derive } from 'store-utils';
import ConnectorsStore from '../../../connectors/stores/connectors-store';
import {
  DependencyEdge,
  DependencyNode,
  FilterByTypesToolTip,
  Issue,
  IssueDetails,
  IssueSortKey,
  IssuesOrder,
} from '../../common/types/issues-types';

export const initialIssuesState: Partial<IssuesStoreState> = {
  issues: [],
  selectedIssue: null,
  totalIssues: 0,
  offset: 0,
  issuesFiltersType: null,
  issuesFiltersTypeSearch: null,
  isModalVisible: false,
  filtersOpen: true,
  order: [IssuesOrder.Desc],
  orderBy: [IssueSortKey.Severity],
  filterIssuesBy: {},
  totalFilteredIssues: 0,
  filtersByTypesToolTip: { category: '', value: '' },
  selectedIssueId: null,
  policyDisableModal: false,
  loadingIssue: false,
  applicationIrrelevantModal: false,
  sliderValuePriority: [0, 100],
  lastDrawerHeight: undefined,
  isFromTopIssues: false,
  showCreateJiraTicketDialog: false,
  showCreateBulkJiraTicketDialog: false,
  showLinkJiraTicketDialog: false,
  showUnlinkJiraTicketDialog: false,
  showSendSlackMessageDialog: false,
  isQuickFixModalVisible: false,
  isQuickFixPermissionDeniedModalVisible: false,
  isFilesDiffModalVisible: false,
  showConfirmExportCSVDialog: false,
  menuIssueId: null,
  applicationStatistics: null,
  totalResolvedIssues: 0,
  totalActiveIssues: 0,
  commentModal: false,
  issueComment: '',
  isAddingCommentLoading: false,
  isDeleteCommentLoading: false,
  loadingJiraTicketsStatus: false,
  isPolicyFixOpen: false,
  searchValues: [],
  searchValue: '',
  changeSeverityModal: false,
  showBulkChangeSeverityModal: false,
  showBulkIssueCommentModal: false,
  showReportFalsePositiveModal: false,
  selectedPosition: { pos: 0 },
  topOffset: 0,
  selected: {},
  allSelected: false,
  isLoadingApplicationStatistics: false,
  filterItems: null,
  showBulkResetSeverityButton: false,
  showBulkEditCommentButton: false,
  showBulkDeleteCommentButton: false,
  conditionalFilters: [],
};

const baseStore = createStore<IssuesStoreState>(
  {
    ...JSON.parse(JSON.stringify(initialIssuesState)),
    loading: createAsyncState(),
    loadingFilters: createAsyncState(),
    loadingSingleFilter: createAsyncState(),
  },
  'Issues Store',
);

const IssuesStore = derive(
  {
    numberOfFilters: get => {
      const { filterIssuesBy, searchValue, conditionalFilters } =
        get(baseStore);
      return getNumberOfSelectedFilters(
        filterIssuesBy,
        searchValue,
        conditionalFilters,
      );
    },
    selectedCount: get => {
      const { selected } = get(baseStore);
      return Object.keys(selected).reduce(
        (acc, key) => (selected[key] ? acc + 1 : acc),
        0,
      );
    },
    isActiveJiraConnection: get => {
      const { connectors } = get(ConnectorsStore);
      const jiraConnector = connectors.find(
        connector => connector.name === 'Jira',
      );
      return Boolean(jiraConnector?.isConfigured);
    },
    connectedTicketingConnectors: (get): Record<string, boolean> => {
      const { connectorsByFamily } = get(ConnectorsStore);
      const ticketManagementFamily = connectorsByFamily.find(
        family => family.family === 'TicketManagement',
      );
      if (!ticketManagementFamily) return {};

      const activeTicketingConnectors =
        ticketManagementFamily.connectors.reduce((acc, connector) => {
          acc[connector.name] = connector.isConfigured;

          return acc;
        }, {} as Record<string, boolean>);
      return activeTicketingConnectors;
    },
    isActiveSlackConnection: get => {
      const { connectors } = get(ConnectorsStore);
      const slackConnector = connectors.find(
        connector => connector.name === 'Slack',
      );
      return Boolean(slackConnector?.isConfigured);
    },
  },

  { proxy: baseStore },
);

export interface IssuesStoreState {
  selected: {
    [x: string]: Nullable<Issue>;
  };
  allSelected: boolean;
  showBulkResetSeverityButton: boolean;
  showBulkEditCommentButton: boolean;
  showBulkDeleteCommentButton: boolean;
  issues: Issue[];
  selectedIssue: Nullable<IssueDetails>;
  selectedIssueId: Nullable<string>;
  totalIssues: number;
  offset: number;
  loading: ReturnType<typeof createAsyncState>;
  loadingJiraTicketsStatus: boolean;
  loadingFilters: ReturnType<typeof createAsyncState>;
  loadingSingleFilter: ReturnType<typeof createAsyncState>;
  issuesFiltersType: Nullable<FilterIssueType>;
  issuesFiltersTypeSearch: Nullable<FilterIssueType>;
  isModalVisible: boolean;
  filtersOpen: boolean;
  order: IssuesOrder[];
  orderBy: IssueSortKey[];
  filterIssuesBy: {};
  totalFilteredIssues: number;
  filtersByTypesToolTip: FilterByTypesToolTip;
  policyDisableModal: boolean;
  loadingIssue: boolean;
  applicationIrrelevantModal: boolean;
  sliderValuePriority: number[];
  lastDrawerHeight?: number;
  isFromTopIssues: boolean;
  showCreateJiraTicketDialog: boolean;
  showLinkJiraTicketDialog: boolean;
  showUnlinkJiraTicketDialog: boolean;
  showSendSlackMessageDialog: boolean;
  showConfirmExportCSVDialog: boolean;
  isQuickFixModalVisible: boolean;
  isQuickFixPermissionDeniedModalVisible: boolean;
  isFilesDiffModalVisible: boolean;
  menuIssueId: Nullable<Issue['id']>;
  applicationStatistics: Nullable<FilterIssueType>;
  totalResolvedIssues: number;
  totalActiveIssues: number;
  commentModal: boolean;
  issueComment: string;
  isAddingCommentLoading: boolean;
  isDeleteCommentLoading: boolean;
  isPolicyFixOpen: boolean;
  searchValues: SearchInput[];
  searchValue: string;
  dependencyGraph: Nullable<{
    nodes: DependencyNode[];
    edges: DependencyEdge[];
    allNodes: DependencyNode[];
    allEdges: DependencyEdge[];
  }>;
  changeSeverityModal: boolean;
  showCreateBulkJiraTicketDialog: boolean;
  showBulkIssueCommentModal: boolean;
  showBulkChangeSeverityModal: boolean;
  showReportFalsePositiveModal: boolean;
  selectedPosition: { pos: number };
  topOffset: number;
  isLoadingApplicationStatistics: boolean;
  filterItems: Nullable<FilterItems>;
  conditionalFilters: ConditionalFilter[];
}

export default IssuesStore;
